import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  Outlet,
} from '@tanstack/react-router'
import { AuthenticatedLayout } from 'components/layout/authenticated-layout'
import { isLoggedIn } from 'helpers/authentication'
// Import all pages
import BannersManagementPage from 'ui/pages/BannersManagementPage'
import ClubManagersPage from 'ui/pages/ClubManagersPage'
import CustomBannersPage from 'ui/pages/CustomBannersPage'
import DashboardPage from 'ui/pages/DashboardPage'
import EditBannerPage from 'ui/pages/EditBannerPage'
import EditCustomBannerPage from 'ui/pages/EditCustomBannerPage'
import EditMembershipCodesPage from 'ui/pages/EditMembershipCodesPage'
import EditReportPage from 'ui/pages/EditReportPage'
import ExtraMenuItemsPage from 'ui/pages/ExtraMenuItemsPage'
import LiveEventDetailPage from 'ui/pages/LiveEventDetailPage'
import LiveEventFormPage from 'ui/pages/LiveEventFormPage'
import LiveEventsPage from 'ui/pages/LiveEventsPage'
import LoginPage from 'ui/pages/LoginPage'
import MatchesPage from 'ui/pages/MatchesPage'
import MembershipCodesPage from 'ui/pages/MembershipCodesPage'
import MessagesPage from 'ui/pages/MessagesPage'
import NotificationsPage from 'ui/pages/NotificationsPage'
import PlayersMerge from 'ui/pages/PlayersMerge'
import PlayersRequestsPage from 'ui/pages/PlayersRequestsPage'
import RankingsPage from 'ui/pages/RankingsPage'
import ReportsPage from 'ui/pages/ReportsPage'
import TweMostClickedPage from 'ui/pages/TweMostClickedPage'
import TweProductPage from 'ui/pages/TweProductPage'
import UpdateTasksPage from 'ui/pages/UpdateTasksPage'
import UsersPage from 'ui/pages/UsersPage'

// Create root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

// Create layout route for protected pages
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: AuthenticatedLayout,
  beforeLoad: async () => {
    if (!isLoggedIn()) {
      throw redirect({
        to: '/accedi',
      })
    }
  },
})

// Create individual routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accedi',
  component: LoginPage,
})

// Create protected routes
const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: DashboardPage,
})

const playersRequestsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/richieste-giocatori',
  component: PlayersRequestsPage,
})

const messagesListRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/messaggi',
  component: MessagesPage,
})

const playersMergeRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/merge-giocatori',
  component: PlayersMerge,
})

const usersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/utenti',
  component: UsersPage,
})

const clubManagersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/utenti/gestori-circoli',
  component: ClubManagersPage,
})

const notificationsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/notifiche',
  component: NotificationsPage,
})

const matchesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/partite',
  component: MatchesPage,
})

const updateTasksRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/aggiornamento-dati',
  component: UpdateTasksPage,
})

const rankingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/cardinalita',
  component: RankingsPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/segnalazioni',
  component: ReportsPage,
})

const customBannersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/custom-banner',
  component: CustomBannersPage,
})

const bannersManagementRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/gestione-banner',
  component: BannersManagementPage,
})

const membershipCodesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/codici-membership',
  component: MembershipCodesPage,
})

const tweMostClickedRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/twe/prodotti-piu-cliccati',
  component: TweMostClickedPage,
})

// Detail is public on the API, but it's reached from the admin-only ranking
const tweProductRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/twe/prodotti/$id',
  component: TweProductPage,
})

const extraMenuItemsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/menu-aggiuntivo',
  component: ExtraMenuItemsPage,
})

const liveEventsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/eventi-live',
  component: LiveEventsPage,
})

const newLiveEventRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/eventi-live/nuovo',
  component: LiveEventFormPage,
})

// Routes with parameters
const liveEventRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/eventi-live/$id',
  component: LiveEventDetailPage,
})

const editLiveEventRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/eventi-live/$id/modifica',
  component: LiveEventFormPage,
})

const editReportRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/segnalazioni/$id/risolvi',
  component: EditReportPage as any,
})

const editCustomBannerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/custom-banner/$id/modifica',
  component: EditCustomBannerPage as any,
})

const editBannerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/gestione-banner/$id/modifica',
  component: EditBannerPage as any,
})

const editMembershipCodesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/codici-membership/$id/modifica',
  component: EditMembershipCodesPage as any,
})

// Create the route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    playersRequestsRoute,
    playersMergeRoute,
    usersRoute,
    clubManagersRoute,
    notificationsRoute,
    matchesRoute,
    updateTasksRoute,
    rankingsRoute,
    reportsRoute,
    editReportRoute,
    customBannersRoute,
    editCustomBannerRoute,
    bannersManagementRoute,
    editBannerRoute,
    membershipCodesRoute,
    editMembershipCodesRoute,
    messagesListRoute,
    tweMostClickedRoute,
    tweProductRoute,
    liveEventsRoute,
    extraMenuItemsRoute,
    newLiveEventRoute,
    liveEventRoute,
    editLiveEventRoute,
  ]),
])

// Create the router
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Handle 404s
  defaultNotFoundComponent: () => <div>404 - Page not found</div>,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
